import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import { clearErrors, deleteProduct, getAdminProducts, deleteProducts } from '../../actions/productAction';
import { deleteBrand, getAllBrands, deleteBrands } from '../../actions/brandAction';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    Box,
    Checkbox,
    Typography,
    Tooltip,
    Chip,
    TextField,
    TableSortLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider
} from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon, Info as InfoIcon } from '@mui/icons-material';
import Rating from '@mui/material/Rating';
import { DELETE_PRODUCT_RESET, DELETE_PRODUCTS_RESET } from '../../constants/productConstants';
import { DELETE_BRAND_RESET, DELETE_BRANDS_RESET } from '../../constants/brandConstants';
import Actions from './Actions';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

const Row = (props) => {
    const { brand, products, selectedProducts, handleProductSelect, deleteProductHandler, deleteBrandHandler } = props;
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [stockFilter, setStockFilter] = useState('all');
    const [ratingFilter, setRatingFilter] = useState(0);
    const maxPrice = products.reduce((max, product) => product.price > max ? product.price : max, 0);
    const [priceRange, setPriceRange] = useState([0, maxPrice]);
    const [priceInput, setPriceInput] = useState(['0', 'Unlimited']);
    const [productSearchTerm, setProductSearchTerm] = useState("");
    const [priceRangeTouched, setPriceRangeTouched] = useState(false);

    useEffect(() => {
        if (!priceRangeTouched) {
            setPriceRange([0, maxPrice]);
        }
    }, [maxPrice, priceRangeTouched]);

    useEffect(() => {
        // This effect synchronizes the input fields when the priceRange state changes.
        setPriceInput([
            priceRange[0].toString(),
            // If the max of the range is the max possible price, display 'Unlimited'
            priceRange[1] === maxPrice ? 'Unlimited' : priceRange[1].toString()
        ]);
    }, [priceRange, maxPrice]);

    const handlePriceInputChange = (e, index) => {
        const newPriceInput = [...priceInput];
        newPriceInput[index] = e.target.value;
        setPriceInput(newPriceInput);
    };

    const handlePriceInputBlur = () => {
        let min = parseInt(priceInput[0], 10);
        let max;

        // If the input is 'Unlimited' or empty, use the maxPrice
        if (priceInput[1].toLowerCase() === 'unlimited' || priceInput[1] === '') {
            max = maxPrice;
        } else {
            max = parseInt(priceInput[1], 10);
        }

        // Fallback to defaults if parsing fails
        if (isNaN(min)) min = 0;
        if (isNaN(max)) max = maxPrice;

        // Clamp values to be within [0, maxPrice]
        if (min < 0) min = 0;
        if (max > maxPrice) max = maxPrice;
        // Ensure min is not greater than max
        if (min > max) {
            min = max;
        }

        setPriceRange([min, max]);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const brandProducts = products.filter(p => p.brand?._id === brand._id);

    const filteredAndSortedBrandProducts = brandProducts
        .filter(product => {
            if (stockFilter === 'inStock') {
                return product.stock > 0;
            }
            if (stockFilter === 'outOfStock') {
                return product.stock === 0;
            }
            return true;
        })
        .filter(product => product.ratings >= ratingFilter)
        .filter(product => product.price >= priceRange[0] && product.price <= priceRange[1])
        .filter(product => product.name.toLowerCase().includes(productSearchTerm.toLowerCase()))
        .sort((a, b) => {
            const isAsc = order === 'asc';
            if (orderBy === 'name') {
                return isAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
            }
            if (orderBy === 'stock') {
                return isAsc ? a.stock - b.stock : b.stock - a.stock;
            }
            if (orderBy === 'price') {
                return isAsc ? a.price - b.price : b.price - a.price;
            }
            return 0;
        });

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    <div className="flex items-center gap-3">
                        <div className="w-24 h-12">
                            <img draggable="false" src={brand.logo.url} alt={brand.name} className="w-full h-full object-contain" />
                        </div>
                        <Typography variant="h6">{brand.name}</Typography>
                        <Tooltip title={brand._id}>
                            <InfoIcon sx={{ fontSize: 16, color: 'gray' }} />
                        </Tooltip>
                    </div>
                </TableCell>
                <TableCell>
                    <Actions editRoute={"brand"} deleteHandler={deleteBrandHandler} id={brand._id} />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <div className="flex justify-between items-center mb-4">
                                <Typography variant="h6" gutterBottom component="div">
                                    Products
                                </Typography>
                                <div className="flex items-center gap-4">
                                    <TextField
                                        label="Search Product"
                                        variant="outlined"
                                        size="small"
                                        value={productSearchTerm}
                                        onChange={(e) => setProductSearchTerm(e.target.value)}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <InputLabel>Stock</InputLabel>
                                        <Select
                                            value={stockFilter}
                                            label="Stock"
                                            onChange={(e) => setStockFilter(e.target.value)}
                                        >
                                            <MenuItem value="all">All</MenuItem>
                                            <MenuItem value="inStock">In Stock</MenuItem>
                                            <MenuItem value="outOfStock">Out of Stock</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div className="flex items-center gap-2">
                                        <Typography>Rating:</Typography>
                                        <Rating
                                            value={ratingFilter}
                                            onChange={(event, newValue) => {
                                                setRatingFilter(newValue || 0);
                                            }}
                                            size="small"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 mb-4">
                                <Typography>Price:</Typography>
                                <TextField
                                    label="Min"
                                    variant="outlined"
                                    size="small"
                                    value={priceInput[0]}
                                    onChange={(e) => handlePriceInputChange(e, 0)}
                                    onBlur={handlePriceInputBlur}
                                />
                                <TextField
                                    label="Max"
                                    variant="outlined"
                                    size="small"
                                    value={priceInput[1]}
                                    onChange={(e) => handlePriceInputChange(e, 1)}
                                    onBlur={handlePriceInputBlur}
                                />
                            </div>
                            <Table size="medium" aria-label="products">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell sortDirection={orderBy === 'name' ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === 'name'}
                                                direction={orderBy === 'name' ? order : 'asc'}
                                                onClick={(e) => handleRequestSort(e, 'name')}
                                            >
                                                Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={orderBy === 'stock' ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === 'stock'}
                                                direction={orderBy === 'stock' ? order : 'asc'}
                                                onClick={(e) => handleRequestSort(e, 'stock')}
                                            >
                                                Stock
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={orderBy === 'price' ? order : false}>
                                            <TableSortLabel
                                                active={orderBy === 'price'}
                                                direction={orderBy === 'price' ? order : 'asc'}
                                                onClick={(e) => handleRequestSort(e, 'price')}
                                            >
                                                Price
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Rating</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAndSortedBrandProducts.map((product) => (
                                        <TableRow key={product._id} sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={selectedProducts.indexOf(product._id) !== -1}
                                                    onChange={(event) => handleProductSelect(event, product._id)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-16 h-16">
                                                        <img draggable="false" src={product.images[0].lowUrl} alt={product.name} className="w-full h-full object-contain" />
                                                    </div>
                                                    <div>
                                                        <Typography variant="body1">{product.name}</Typography>
                                                        <Tooltip title={product._id}>
                                                            <InfoIcon sx={{ fontSize: 14, color: 'gray' }} />
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={product.stock > 0 ? "In Stock" : "Out of Stock"}
                                                    color={product.stock > 0 ? "success" : "error"}
                                                    size="small"
                                                />
                                                <Typography variant="body2" sx={{ mt: 1 }}>{product.stock} units</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body1" className="font-semibold">
                                                    {`₹${product.price.toLocaleString()}`}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Rating readOnly value={product.ratings} size="small" precision={0.5} />
                                            </TableCell>
                                            <TableCell>
                                                <Actions editRoute={"product"} deleteHandler={deleteProductHandler} id={product._id} viewRoute={`/product/${product._id}`} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}


const ProductTable = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    const { products, error } = useSelector((state) => state.products);
    const { loading, isDeleted, error: deleteError, isProductsDeleted, error: deleteProductsError } = useSelector((state) => state.product);
    const { brands } = useSelector((state) => state.brands);
    const { loading: brandLoading, isDeleted: isBrandDeleted, error: deleteBrandError, isBrandsDeleted, error: deleteBrandsError } = useSelector((state) => state.brandDetails);

    const [productSelection, setProductSelection] = useState([]);
    const [brandSelection, setBrandSelection] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (deleteError) {
            enqueueSnackbar(deleteError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isDeleted) {
            enqueueSnackbar("Product Deleted Successfully", { variant: "success" });
            dispatch({ type: DELETE_PRODUCT_RESET });
        }
        if (deleteProductsError) {
            enqueueSnackbar(deleteProductsError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isProductsDeleted) {
            enqueueSnackbar("Products Deleted Successfully", { variant: "success" });
            dispatch({ type: DELETE_PRODUCTS_RESET });
            setProductSelection([]);
        }
        if (deleteBrandError) {
            enqueueSnackbar(deleteBrandError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isBrandDeleted) {
            enqueueSnackbar("Brand Deleted Successfully", { variant: "success" });
            dispatch({ type: DELETE_BRAND_RESET });
        }
        if (deleteBrandsError) {
            enqueueSnackbar(deleteBrandsError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isBrandsDeleted) {
            enqueueSnackbar("Brands Deleted Successfully", { variant: "success" });
            dispatch({ type: DELETE_BRANDS_RESET });
            setBrandSelection([]);
        }
        dispatch(getAdminProducts());
        dispatch(getAllBrands());
    }, [dispatch, error, deleteError, isDeleted, enqueueSnackbar, deleteBrandError, isBrandDeleted, isProductsDeleted, deleteProductsError, isBrandsDeleted, deleteBrandsError, isBrandsDeleted]);

    const deleteProductHandler = (id) => {
        dispatch(deleteProduct(id));
    }

    const deleteProductsHandler = (ids) => {
        dispatch(deleteProducts(ids));
    }

    const deleteBrandHandler = (id) => {
        dispatch(deleteBrand(id));
    }

    const deleteBrandsHandler = (ids) => {
        dispatch(deleteBrands(ids));
    }

    const handleProductSelect = (event, id) => {
        const selectedIndex = productSelection.indexOf(id);
        let newSelection = [];

        if (selectedIndex === -1) {
            newSelection = newSelection.concat(productSelection, id);
        } else if (selectedIndex === 0) {
            newSelection = newSelection.concat(productSelection.slice(1));
        } else if (selectedIndex === productSelection.length - 1) {
            newSelection = newSelection.concat(productSelection.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelection = newSelection.concat(
                productSelection.slice(0, selectedIndex),
                productSelection.slice(selectedIndex + 1),
            );
        }
        setProductSelection(newSelection);
    };

    const handleBrandSelect = (event, id) => {
        const selectedIndex = brandSelection.indexOf(id);
        let newSelection = [];

        if (selectedIndex === -1) {
            newSelection = newSelection.concat(brandSelection, id);
        } else if (selectedIndex === 0) {
            newSelection = newSelection.concat(brandSelection.slice(1));
        } else if (selectedIndex === brandSelection.length - 1) {
            newSelection = newSelection.concat(brandSelection.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelection = newSelection.concat(
                brandSelection.slice(0, selectedIndex),
                brandSelection.slice(selectedIndex + 1),
            );
        }
        setBrandSelection(newSelection);
    }

    const filteredBrands = brands?.filter((brand) =>
        brand.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <MetaData title="Admin Products - AmritLok" />

            {(loading || brandLoading) && <BackdropLoader />}

            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Products by Brand</h1>
                <div className="flex gap-4 items-center">
                    <TextField
                        label="Search Brand"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {productSelection.length > 0 && (
                        <button
                            onClick={() => deleteProductsHandler(productSelection)}
                            className="py-2 px-4 rounded-lg shadow-lg font-medium text-white bg-red-600 hover:bg-red-700"
                        >
                            Delete Selected Products ({productSelection.length})
                        </button>
                    )}
                    {brandSelection.length > 0 && (
                        <button
                            onClick={() => deleteBrandsHandler(brandSelection)}
                            className="py-2 px-4 rounded-lg shadow-lg font-medium text-white bg-red-600 hover:bg-red-700"
                        >
                            Delete Selected Brands ({brandSelection.length})
                        </button>
                    )}
                    <Link to="/admin/new_product" className="py-2 px-4 rounded-lg shadow-lg font-medium text-white transition-transform transform hover:scale-105" style={{ backgroundColor: theme.palette.primary.main }}>New Product</Link>
                    <Link to="/admin/new_brand" className="py-2 px-4 rounded-lg shadow-lg font-medium text-white transition-transform transform hover:scale-105" style={{ backgroundColor: theme.palette.primary.main }}>New Brand</Link>
                </div>
            </div>
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full"
            >
                <TableContainer component={Paper}>
                    <Table aria-label="collapsible table">
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setBrandSelection(brands.map(b => b._id));
                                                } else {
                                                    setBrandSelection([]);
                                                }
                                            }}
                                        />
                                        Brand
                                    </div>
                                </TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredBrands && filteredBrands.map((brand) => (
                                <Row
                                    key={brand._id}
                                    brand={brand}
                                    products={products}
                                    selectedProducts={productSelection}
                                    handleProductSelect={handleProductSelect}
                                    deleteProductHandler={deleteProductHandler}
                                    deleteBrandHandler={deleteBrandHandler}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </motion.div>
        </>
    );
};

export default ProductTable;